import { CloudOff, Settings2, TriangleAlert } from 'lucide-react';
import { asNotConfigured, adminUrl, isUnreachable, type NotConfigured } from '../lib/not-configured';
import { Button, EmptyState } from './basics';

/**
 * The honest empty state for a feature whose outside account or key is not set up (docs/PRODUCTION-POLICY.md).
 * Pass the caught error (`<NeedsSetup error={err} />`) or explicit details. Renders nothing for other errors.
 */
export function NeedsSetup({ error, details }: { error?: unknown; details?: NotConfigured | null }) {
  const d = details ?? asNotConfigured(error);
  if (!d) return null;
  return (
    <div role="status" data-testid="needs-setup">
      <EmptyState
        icon={Settings2}
        title={`${d.setting} needs setup`}
        purpose={`${d.message}${d.needs ? ` You will need: ${d.needs}.` : ''}`}
        action={<a className="btn" href={adminUrl(d.adminPath)} target="_blank" rel="noreferrer">Set this up in the admin portal</a>}
      />
    </div>
  );
}

/**
 * What a screen shows when its data did not load: NeedsSetup for a not-configured error, a clear offline state with
 * Retry when the API cannot be reached, otherwise the error message with Retry. Never shows stand-in data.
 */
export function ErrorState({ error, what = 'This', onRetry }: { error: unknown; what?: string; onRetry?: () => void }) {
  if (asNotConfigured(error)) return <NeedsSetup error={error} />;
  const retry = onRetry ? <Button variant="primary" onClick={onRetry}>Try again</Button> : undefined;
  if (isUnreachable(error)) {
    return (
      <div role="status" data-testid="offline-state">
        <EmptyState icon={CloudOff} title="Can't reach Crosspoint AI +" purpose="Nothing was lost. Check your connection, then try again." action={retry} />
      </div>
    );
  }
  const message = error instanceof Error && error.message ? error.message : 'Something went wrong.';
  return (
    <div role="alert" data-testid="error-state">
      <EmptyState icon={TriangleAlert} title={`${what} did not load`} purpose={message} action={retry} />
    </div>
  );
}
